import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Target, LineChart, ListTodo, Users, FileText, Compass, Calendar } from "lucide-react";
import { Link } from "react-router-dom";

const stat = (label, value, mono = true) => (
  <div className="p-6 border-r border-b border-neutral-200 bg-white">
    <div className="mono text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
    <div className={`${mono ? "mono" : "font-display"} text-4xl font-bold mt-3`}>{value}</div>
  </div>
);

export default function Overview() {
  const { user } = useAuth();
  const [s, setS] = useState(null);

  useEffect(() => { api.get("/summary").then((r) => setS(r.data)); }, []);

  return (
    <div className="p-6 lg:p-8">
      <div className="mono text-xs text-neutral-500 uppercase tracking-widest">Runwell OS · {user?.company}</div>
      <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-2">Good morning, {user?.name?.split(" ")[0]}.</h1>
      <p className="text-neutral-600 mt-2">Here's your operating snapshot for this week.</p>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 border-l border-t border-neutral-200" data-testid="overview-stats">
        {stat("KPIs Tracked", s?.kpis ?? "—")}
        {stat("Rocks On Track", s?.rocks_on_track ?? "—")}
        {stat("Rocks Off Track", s?.rocks_off_track ?? "—")}
        {stat("Open Issues", s?.issues_open ?? "—")}
        {stat("Rocks Done", s?.rocks_done ?? "—")}
        {stat("Processes", s?.processes ?? "—")}
        {stat("Seats", s?.seats ?? "—")}
        {stat("Last L10", s?.last_meeting ? s.last_meeting.date : "None yet", false)}
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-neutral-200 bg-white p-6">
          <div className="mono text-xs uppercase text-neutral-500 tracking-widest">This Week's Rhythm</div>
          <div className="mt-4 space-y-3">
            {[
              { t: "L10 Weekly Meeting", d: "Wednesdays · 90 min · Segue, Scorecard, Rocks, Headlines, To-dos, IDS, Conclude." },
              { t: "Scorecard Review", d: "Every Monday — red metrics become issues." },
              { t: "Rocks Check-in", d: "Report status each week. Off-track → to the issues list." },
            ].map((x) => (
              <div key={x.t} className="border-t border-neutral-200 pt-3">
                <div className="font-display font-bold">{x.t}</div>
                <div className="text-sm text-neutral-600 mt-1">{x.d}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-neutral-200 bg-white p-6">
          <div className="mono text-xs uppercase text-neutral-500 tracking-widest">Jump into a module</div>
          <div className="mt-4 grid grid-cols-2 gap-2">
            {[
              { to: "/app/vision", icon: Compass, label: "Vision" },
              { to: "/app/scorecard", icon: LineChart, label: "Scorecard" },
              { to: "/app/rocks", icon: Target, label: "Rocks" },
              { to: "/app/issues", icon: ListTodo, label: "Issues" },
              { to: "/app/meetings", icon: Calendar, label: "Meetings" },
              { to: "/app/accountability", icon: Users, label: "Seats" },
              { to: "/app/processes", icon: FileText, label: "Processes" },
            ].map((m) => (
              <Link key={m.to} to={m.to} className="flex items-center gap-2 px-3 py-3 border border-neutral-200 hover:border-neutral-900 hover:bg-neutral-50 rounded-sm" data-testid={`overview-jump-${m.label.toLowerCase()}`}>
                <m.icon className="h-4 w-4 text-[#E54D2E]" strokeWidth={1.75} />
                <span className="text-sm">{m.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
